/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */

import {
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconEdit,
  IconPencil,
  IconEye,
  IconPlus,
  IconSearch,
  IconX,
  IconStack,
  IconDatabase,
  IconFileText,
  IconTag,
} from "@tabler/icons-react";
import NormalSearch from "../../../components/common/NormalSearch";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import { useNavigate } from "react-router-dom";
import ToggleButton from "../../../components/ToggleButton";
import RoleToggle from "../../../components/RoleToggle";
import {
  useFetchProductItemData,
  useUpdateProductItemStatus,
} from "../../../apis/productApis";
import React, { useEffect, useState, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import { handleDownload } from "../../../utilities/downloadHelper";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import CreatePromoModal from "./CreatePromoModal";
import AddItem from "../add-item";
import {
  useItemPromoCreateMutation,
  usePromoUpdateMutation,
} from "../../../apis/promoApis";
import { usePermission } from "../../../hooks/usePermissionTabs";
import ButtonStyled from "../../../components/common/ButtonStyled";

type ProductData = {
  id: string;
};

export default function ItemTab({
  setHeaderButtons,
}: { setHeaderButtons?: (n: React.ReactNode) => void } = {}) {
  const { allowed: canCreateProduct } = usePermission("products", "create");
  const { allowed: canUpdateProduct } = usePermission("products", "update");
  const { allowed: canExportProduct } = usePermission("products", "export");
  const { allowed: canCreatePromo } = usePermission("promo", "create");
  const { allowed: canUpdatePromo } = usePermission("promo", "update");

  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<ProductData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  const expandedIdsRef = useRef<Set<string>>(new Set());
  const [, forceUpdate] = useState(0);
  const [activeSubTabs, setActiveSubTabs] = useState<Record<string, string>>(
    {},
  );

  // States for Promo Modal
  const [isPromoOpen, setIsPromoOpen] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState<any>(null);
  const [promoModalMode, setPromoModalMode] = useState<"add" | "view" | "edit">("add");

  // States for Add Product Side panel
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | undefined>(
    undefined,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const addItemRef = useRef<{ submit: () => void }>(null);

  // Redux Profile for client info
  const profile = useSelector((state: any) => state?.auth?.profile);

  // Promo queries & mutations
  const { mutate: createPromo, isPending: promoCreating } =
    useItemPromoCreateMutation();
  const { mutate: updatePromo, isPending: promoUpdating } =
    usePromoUpdateMutation();

  // Form setup for Promo
  const {
    register: registerPromo,
    handleSubmit: handleSubmitPromo,
    formState: { errors: promoErrors },
    clearErrors: clearPromoErrors,
    reset: resetPromo,
    setValue: setPromoValue,
    watch: watchPromo,
  } = useForm();

  const handleAddPromo = () => {
    setSelectedPromo(null);
    resetPromo();
    setPromoModalMode("add");
    setIsAddProductOpen(false);
    setIsPromoOpen(true);
  };

  const handleViewPromo = (promo: any) => {
    setSelectedPromo(promo);
    setPromoModalMode("view");
    setIsAddProductOpen(false);
    setIsPromoOpen(true);
    resetPromo();
    setPromoValue("toClient", promo.clientId);
    setPromoValue("startEndDate", promo.startEndDate);
    setPromoValue("promoCode", promo.promoCode);
    if (promo.products && promo.products.length > 0) {
      setPromoValue(
        "products",
        promo.products.map((product: any) => ({
          productId: product.productId || "",
          productName: product.productName || "",
          quantity: product.quantity || "",
        })),
      );
    } else {
      setPromoValue("products", [
        { productId: "", productName: "", quantity: "" },
      ]);
    }
  };

  const onSubmitPromo = (values: any, mode: "add" | "edit") => {
    const payload = {
      clientType: "LM",
      toClient: profile?.id,
      startEndDate: values.startEndDate,
      promoCode: values.promoCode,
      productAndQuantity: watchPromo("products")
        ?.filter((product: any) => product?.productId)
        ?.map((productData: any) => {
          return {
            ...productData,
            quantity: parseInt(productData?.quantity),
          };
        }),
    };

    if (mode === "edit" && selectedPromo) {
      updatePromo(
        { id: selectedPromo.id, ...payload },
        {
          onSuccess: () => {
            toast.success("Promo updated successfully");
            resetPromo();
            setIsPromoOpen(false);
            refetch();
          },
          onError: (err: any) => toast.error(err?.message || "Update failed"),
        },
      );
    } else {
      createPromo(payload, {
        onSuccess: () => {
          toast.success("Promo created successfully");
          resetPromo();
          setIsPromoOpen(false);
          refetch();
        },
        onError: (err: any) => toast.error(err?.message || "Create failed"),
      });
    }
  };

  const isAnySidePanelOpen = isPromoOpen || isAddProductOpen;

  const gridColsClass = isAnySidePanelOpen
    ? "grid-cols-[40px_56px_1.2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr]"
    : "grid-cols-[40px_56px_1.2fr_1fr_1fr_1fr_0.8fr_0.8fr_0.8fr_1fr_0.8fr_1fr_auto]";

  const debouncedSearchFor = useDebounce(searchFor, 550);

  const { isLoading, isFetching, error, refetch } = useFetchProductItemData(
    pageNo,
    limit,
    debouncedSearchFor,
  );

  const data = useSelector((state: any) => state?.product?.data);

  const { mutate: updateProductStatus, isPending: statusPending } =
    useUpdateProductItemStatus();

  const handleToggleStatus = (item: any) => {
    setSelectedItem(item);
    setActionModal(true);
  };

  const confirmUpdateStatus = async () => {
    const newStatus = selectedItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    updateProductStatus(
      { id: selectedItem?.id, status: newStatus },
      {
        onSuccess: () => {
          refetch();
          toast.success("Status updated successfully");
          setActionModal(false);
        },
        onError: (error) => {
          toast.error(`${error}`);
        },
      },
    );
  };

  const toggleRow = (id: string) => {
    if (expandedIdsRef.current.has(id)) {
      expandedIdsRef.current.delete(id);
    } else {
      expandedIdsRef.current.add(id);
    }
    forceUpdate((n) => n + 1);
  };

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    if (data?.productItems) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.productItems.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.productItems, selectedRows]);

  const [columnsDataState, setColumnsData] = useState([
    { text: "Image", value: "image", isShow: true },
    { text: "Product Name", value: "itemName", isShow: true },
    { text: "SKU", value: "sku", isShow: true },
    { text: "Category", value: "category", isShow: true },
    { text: "Storage Type", value: "storageType", isShow: true },
    { text: "Capacity", value: "capacity", isShow: true },
    { text: "HS Code", value: "hsCode", isShow: true },
    { text: "Dimensions (L x W x H)", value: "dimensions", isShow: true },
    { text: "Weight", value: "weight", isShow: true },
    { text: "Created By", value: "createdByName", isShow: true },
    { text: "Created At", value: "createdAt", isShow: true },
    { text: "Status", value: "status", isShow: true },
  ]);

  const columnsData = useMemo(() => {
    if (isAnySidePanelOpen) {
      return columnsDataState.map((c) => ({
        ...c,
        isShow:
          c.isShow &&
          !["createdByName", "createdAt", "status"].includes(c.value),
      }));
    }
    return columnsDataState;
  }, [columnsDataState, isAnySidePanelOpen]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  // Calculate dynamic stats
  const inventoryStats = useMemo(() => {
    const total = data?.total || 0;
    const active =
      data?.productItems?.filter((p: any) => p?.status === "ACTIVE").length ||
      0;
    const categories =
      new Set(data?.productItems?.map((p: any) => p?.category)).size || 0;

    const totalCapacity =
      data?.productItems?.reduce((acc: number, item: any) => {
        let totalVal = 0;
        try {
          let storageArray: any[] = [];
          if (Array.isArray(item.storage)) {
            storageArray = item.storage.map((s: any) =>
              typeof s === "string" ? JSON.parse(s) : s,
            );
          } else if (typeof item.storage === "string") {
            storageArray = [JSON.parse(item.storage)];
          } else {
            storageArray = [item.storage];
          }
          totalVal = storageArray.reduce(
            (sum: number, s: any) => sum + Number(s.capacity || 0),
            0,
          );
        } catch {
          totalVal = 0;
        }
        return acc + totalVal;
      }, 0) || 0;

    return { total, active, categories, totalCapacity };
  }, [data]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateStr;
    }
  };

  const mapDataToExport = (c: any, index: number) => {
    let capacity = 0;
    let storageType = "-";
    try {
      let storageArray: any[] = [];
      if (Array.isArray(c.storage)) {
        storageArray = c.storage.map((s: any) =>
          typeof s === "string" ? JSON.parse(s) : s,
        );
      } else if (typeof c.storage === "string") {
        storageArray = [JSON.parse(c.storage)];
      } else {
        storageArray = [c.storage];
      }
      capacity = storageArray.reduce(
        (sum: number, s: any) => sum + Number(s.capacity || 0),
        0,
      );
      if (storageArray.length > 0) {
        storageType =
          storageArray
            .map((s) => s.type || s.storageType || "")
            .filter(Boolean)
            .join(", ") || "-";
      }
    } catch {
      capacity = 0;
    }

    return {
      "S/N": index + 1,
      "Item Name": c.itemName,
      SKU: c.sku,
      Category: c.category,
      "Storage Type": storageType,
      Capacity: capacity,
      "Hs Code": c.hsCode,
      "Dimensions (L x W x H)":
        c.length && c.width && c.height
          ? `${c.length}x${c.width}x${c.height}`
          : c.dimensions || "-",
      Weight: c.weight,
      "Created By": c.createdByName,
      "Created At": formatDate(c.createdAt),
      Status: c.status,
    };
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-full rounded-r-3xl">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

        {/* Left Column: Product Table Card */}
        <div
          className={
            isAnySidePanelOpen ? "col-span-12 xl:col-span-7" : "col-span-12"
          }
        >
          <div className="bg-white/20 rounded-3xl overflow-visible space-y-2">
            <div className="px-1 py-2 flex flex-col md:flex-row md:items-center justify-between gap-4 select-none">
              {/* Left side: Brand badge */}
              <div className="flex flex-wrap items-center gap-3"></div>

              {/* Right side: Controls */}
              <div className="flex items-center flex-wrap gap-2">
                {/* Sliding Search */}
                <div className="relative flex items-center">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-64 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-[#212121] focus:outline-none flex-shrink-0"
                      title="Search"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          value={searchFor}
                          onChange={(e) => {
                            setSearchFor(e.target.value);
                            setPageNo(1);
                          }}
                          placeholder="Search products..."
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-sm text-[#212121] placeholder-slate-400 outline-none border-none"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchFor("");
                            setShowSearch(false);
                          }}
                          className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          title="Clear & Close"
                        >
                          <IconX size={16} stroke={2.5} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Download Excel */}
                {canExportProduct && (
                  <button
                    onClick={() =>
                      handleDownload({
                        selectedRows,
                        allSelectedData,
                        setSelectedRows,
                        setAllSelectedData,
                        showCheckboxes,
                        setShowCheckboxes,
                        setColumnsData,
                        mapDataToExport,
                        fileName: "product_list.xlsx",
                      })
                    }
                    className={`w-8 h-8 flex items-center justify-center border border-slate-200 bg-white text-[#212121] hover:bg-slate-50 transition-all duration-150 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                      showCheckboxes && selectedRows.length === 0
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    title="Download Excel"
                    disabled={showCheckboxes && selectedRows.length === 0}
                  >
                    <IconDownload stroke={1.5} size={17} />
                  </button>
                )}

                {/* Entries Selector */}
                <EntriesSelector
                  value={limit}
                  options={[10, 20, 30, 50]}
                  onChange={(val) => {
                    setLimit(val);
                    setPageNo(1);
                  }}
                />

                {/* Columns Filter */}
                <ColumnsFilter
                  columnsData={columnsDataState.filter(
                    (c) => c.value !== "select",
                  )}
                  setHeadings={setHeadings}
                />

                {/* Add Product */}
                {canCreateProduct && (
                  <ButtonStyled
                    type="button"
                    onClick={() => {
                      setIsPromoOpen(false);
                      setEditingProductId(undefined);
                      setIsEditMode(false);
                      setIsAddProductOpen(true);
                    }}
                    variant={isAddProductOpen ? "solid" : "outline"}
                    icon={<IconPlus className="w-3.5 h-3.5" />}
                    value="Add Item"
                  />
                )}
              </div>
            </div>

            <div>
              {isLoading || isFetching ? (
                <div className="p-4">
                  <TableSkeleton
                    columns={columnsData
                      .filter((c) => c.isShow)
                      .map((c) => c.text)}
                    rows={6}
                  />
                </div>
              ) : data?.productItems?.length ? (
                <>
                  {/* Header Row with Grid */}
                  <div
                    className={`grid ${gridColsClass} items-center gap-2 border-b border-t border-dashed border-[#ECECEC] py-2 px-4 text-xs font-semibold text-[#212121] uppercase`}
                  >
                    {isAnySidePanelOpen ? (
                      <>
                        <div className="col-span-1 text-center"> </div>
                        <div className="col-span-1 text-center">IMAGE</div>
                        <div className="col-span-1 text-start">NAME</div>
                        <div className="col-span-1 text-start">SKU</div>
                        <div className="col-span-1 text-start">
                          CATEGORY
                        </div>
                        <div className="col-span-1 text-start">STORAGE</div>
                        <div className="col-span-1 text-start">
                          CAPACITY
                        </div>
                        <div className="col-span-1 text-start">HS CODE</div>
                        <div className="col-span-1 text-start">
                          DIMENSIONS
                        </div>
                        <div className="col-span-1 text-start">WEIGHT</div>
                      </>
                    ) : (
                      <>
                        <div className="col-span-1 text-center"> </div>
                        <div className="col-span-1 text-center">IMAGE</div>
                        <div className="col-span-1 text-start">NAME</div>
                        <div className="col-span-1 text-start">SKU</div>
                        <div className="col-span-1 text-start">
                          CATEGORY
                        </div>
                        <div className="col-span-1 text-start">STORAGE</div>
                        <div className="col-span-1 text-start">
                          CAPACITY
                        </div>
                        <div className="col-span-1 text-start">HS CODE</div>
                        <div className="col-span-1 text-start">
                          DIMENSIONS
                        </div>
                        <div className="col-span-1 text-start">WEIGHT</div>
                        <div className="col-span-1 text-start">
                          CREATED BY
                        </div>
                        <div className="col-span-1 text-start">
                          CREATED AT
                        </div>
                        <div className="col-span-1 text-start">STATUS</div>
                      </>
                    )}
                  </div>

                  {/* Product Rows */}
                  {data?.productItems?.map((item: any) => {
                    const expanded = expandedIdsRef.current.has(item.id);
                    let capacity = 0;
                    let storageType = "-";
                    try {
                      let storageArray: any[] = [];
                      if (Array.isArray(item.storage)) {
                        storageArray = item.storage.map((s: any) =>
                          typeof s === "string" ? JSON.parse(s) : s,
                        );
                      } else if (typeof item.storage === "string") {
                        storageArray = [JSON.parse(item.storage)];
                      } else {
                        storageArray = [item.storage];
                      }
                      capacity = storageArray.reduce(
                        (sum: number, s: any) =>
                          sum + Number(s.capacity || 0),
                        0,
                      );
                      if (storageArray.length > 0) {
                        storageType =
                          storageArray
                            .map((s) => s.type || s.storageType || "")
                            .filter(Boolean)
                            .join(", ") || "-";
                      }
                    } catch {
                      capacity = 0;
                    }

                    return (
                      <div
                        key={item.id}
                        className="bg-white border border-[#ECECEC] rounded-2xl p-4 my-1 shadow-sm hover:shadow-md transition-all"
                      >
                        {/* Main Row */}
                        <div
                          className={`grid ${gridColsClass} items-center gap-2 cursor-pointer text-sm text-[#212121]`}
                          onClick={() => toggleRow(item.id)}
                        >
                          {/* Expand button */}
                          <div className="flex items-center justify-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRow(item.id);
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#ECECEC] bg-[#F7F6F2] hover:bg-gray-100 transition"
                            >
                              {expanded ? (
                                <IconChevronUp
                                  size={16}
                                  className="text-[#404040]"
                                />
                              ) : (
                                <IconChevronDown
                                  size={16}
                                  className="text-[#404040]"
                                />
                              )}
                            </button>
                          </div>

                          {/* Image */}
                          <div className="w-12 h-12 rounded-xl overflow-hidden border border-[#ECECEC] flex-shrink-0">
                            <img
                              src={
                                item.image || "/img/product-placeholder.png"
                              }
                              alt={item.itemName}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Name */}
                          <div className="truncate font-medium text-start">
                            {item.itemName}
                          </div>

                          {/* SKU */}
                          <div className="text-xs truncate text-start font-mono">
                            {item.sku}
                          </div>

                          {/* Category */}
                          <div className="text-xs text-start">
                            {item.category || "-"}
                          </div>

                          {/* Storage Type */}
                          <div className="text-xs text-start">
                            {storageType}
                          </div>

                          {/* Capacity */}
                          <div className="text-xs text-start">
                            {capacity}
                          </div>

                          {/* Hs Code */}
                          <div className="text-xs text-start">
                            {item.hsCode || "-"}
                          </div>

                          {/* Dimensions */}
                          <div className="text-xs text-start">
                            {[item.length, item.width, item.height]
                              .filter((v) => v !== null && v !== undefined)
                              .join(" × ") || "-"}
                          </div>

                          {/* Weight */}
                          <div className="text-xs text-start">
                            {item.weight || "-"}
                          </div>

                          {/* Only show these columns when side panel is closed */}
                          {!isAnySidePanelOpen && (
                            <>
                              {/* Created By */}
                              <div className="text-xs text-start">
                                {item.createdByName || "-"}
                              </div>

                              {/* Created At */}
                              <div className="text-xs text-start">
                                {formatDate(item.createdAt)}
                              </div>

                              {/* Status */}
                              <div className="text-start">
                                <button
                                  disabled={!canUpdateProduct}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(item);
                                  }}
                                  className={`px-3 py-1 rounded-full border text-[11px] font-semibold capitalize transition ${
                                    item?.status === "ACTIVE" || item?.status === true || item?.status === "active"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                      : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                                  } ${!canUpdateProduct ? "opacity-60 cursor-not-allowed" : ""}`}
                                >
                                  {item.status
                                    ? typeof item.status === "string"
                                      ? item.status.toLowerCase()
                                      : "active"
                                    : "active"}
                                </button>
                              </div>
                            </>
                          )}
                        </div>

                            {/* Expanded Content */}
                            {expanded && (
                              <div className="mt-4 border-t border-[#ECECEC] pt-4">
                                {(() => {
                                  const activeTab =
                                    activeSubTabs[item.id] || "variants";
                                  const subTabs = [
                                    {
                                      id: "variants",
                                      label: `Variants (${item.variants?.length || 0})`,
                                      icon: (
                                        <IconStack
                                          size={14}
                                          className="mr-1.5"
                                        />
                                      ),
                                    },
                                    {
                                      id: "promotions",
                                      label: `Promotions (${item.promos?.length || 0})`,
                                      icon: (
                                        <IconTag size={14} className="mr-1.5" />
                                      ),
                                    },
                                    {
                                      id: "terms",
                                      label: "Terms & Conditions",
                                      icon: (
                                        <IconFileText
                                          size={14}
                                          className="mr-1.5"
                                        />
                                      ),
                                    },
                                  ];

                                  return (
                                    <>
                                      {/* Sub Tab Bar with Action Button */}
                                      <div className="flex items-center justify-between mb-4 pb-2 border-b border-[#ECECEC] select-none">
                                        <div className="flex flex-wrap items-center gap-2">
                                          {subTabs.map((tab) => {
                                            const isActive =
                                              activeTab === tab.id;
                                            return (
                                              <button
                                                key={tab.id}
                                                onClick={() =>
                                                  setActiveSubTabs((prev) => ({
                                                    ...prev,
                                                    [item.id]: tab.id,
                                                  }))
                                                }
                                                className={`flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg border transition duration-150 ${
                                                  isActive
                                                    ? "bg-amber-50 border-amber-300 text-amber-700 font-bold"
                                                    : "bg-transparent border-transparent text-[#8A8A8A] hover:text-[#212121]"
                                                }`}
                                              >
                                                {tab.icon}
                                                <span>{tab.label}</span>
                                              </button>
                                            );
                                          })}
                                        </div>

                                        {/* Action (Edit) Button — exact Last-Mile style */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setIsPromoOpen(false);
                                            setIsEditMode(true);
                                            setEditingProductId(item.id);
                                            setIsAddProductOpen(true);
                                          }}
                                          className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#ECECEC] bg-white hover:bg-[#F9F9F9] transition"
                                          title={
                                            canUpdateProduct
                                              ? "Edit Details"
                                              : "View Details"
                                          }
                                        >
                                          {canUpdateProduct ? (
                                            <IconPencil
                                              size={18}
                                              className="text-[#404040]"
                                            />
                                          ) : (
                                            <IconEye
                                              size={18}
                                              className="text-[#404040]"
                                            />
                                          )}
                                        </button>
                                      </div>

                                      {/* Tab Panels */}
                                      <div>
                                        {/* Tab 1: Variants */}
                                        {activeTab === "variants" && (
                                          <div>
                                            {item.variants &&
                                            item.variants.length > 0 ? (
                                              <div className="overflow-x-auto rounded-xl border border-[#ECECEC] bg-white">
                                                <table className="min-w-full divide-y divide-[#ECECEC] text-sm">
                                                  <thead className="bg-[#F7F6F2] text-xs font-semibold uppercase text-[#8A8A8A]">
                                                    <tr>
                                                      <th className="px-4 py-2 text-left">
                                                        Name
                                                      </th>
                                                      <th className="px-4 py-2 text-left">
                                                        Colour
                                                      </th>
                                                      <th className="px-4 py-2 text-left">
                                                        SKU / CODE
                                                      </th>
                                                      <th className="px-4 py-2 text-left">
                                                        HS Code
                                                      </th>
                                                      <th className="px-4 py-2 text-left">
                                                        Storage
                                                      </th>
                                                      <th className="px-4 py-2 text-left">
                                                        Capacity
                                                      </th>
                                                      <th className="px-4 py-2 text-left">
                                                        Size
                                                      </th>
                                                      <th className="px-4 py-2 text-left">
                                                        Weight
                                                      </th>
                                                      <th className="px-4 py-2 text-center">
                                                        Status
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-[#ECECEC] bg-white">
                                                    {item.variants.map(
                                                      (
                                                        variant: any,
                                                        idx: number,
                                                      ) => (
                                                        <tr
                                                          key={variant.id}
                                                          className={
                                                            idx % 2 === 0
                                                              ? "bg-white"
                                                              : "bg-[#FAFAFA]"
                                                          }
                                                        >
                                                          <td className="px-4 py-2 text-xs text-gray-600 truncate max-w-[150px]" title={typeof item.itemName === "object" ? item.itemName?.en || item.itemName?.ar : item.itemName || "-"}>
                                                            {typeof item.itemName === "object"
                                                              ? item.itemName?.en || item.itemName?.ar
                                                              : item.itemName || "-"}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-gray-600">
                                                            {variant.itemInfo?.color ||
                                                              variant.optionValue ||
                                                              variant.variantName ||
                                                              "-"}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-gray-600 truncate max-w-[120px]" title={variant.sku || "-"}>
                                                            {variant.sku || "-"}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-gray-600">
                                                            {variant.hsCode || "-"}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-gray-600">
                                                            {storageType}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-gray-600">
                                                            {item.storage?.[0]?.capacity || "-"}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-gray-600">
                                                            {variant.itemInfo?.size || "-"}
                                                          </td>
                                                          <td className="px-4 py-2 text-xs text-gray-600">
                                                            {variant.weight || "-"}
                                                          </td>
                                                          <td className="px-4 py-2 flex justify-center items-center text-center">
                                                            <ToggleButton
                                                              checked={
                                                                variant.status ===
                                                                "ACTIVE"
                                                              }
                                                            />
                                                          </td>
                                                        </tr>
                                                      ),
                                                    )}
                                                  </tbody>
                                                </table>
                                              </div>
                                            ) : (
                                              <div className="p-4 text-center border border-dashed border-[#ECECEC] rounded-xl text-xs text-[#8A8A8A] bg-white">
                                                Variants not enabled for this
                                                SKU hierarchy.
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {/* Tab 2: Promotions */}
                                        {activeTab === "promotions" && (
                                          <div>
                                            {(() => {
                                              const activePromos =
                                                item.promos || [];
                                              const hasVariants =
                                                item.variants &&
                                                item.variants.length > 0;
                                              return activePromos.length > 0 ? (
                                                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                                                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                                                    <thead className="bg-[#F7F6F2] text-xs font-bold uppercase text-[#131327]/70 border-b border-dashed border-gray-200">
                                                      <tr>
                                                        <th className="px-4 py-2 text-left">
                                                          PROMO CODE
                                                        </th>
                                                        {hasVariants && (
                                                          <th className="px-4 py-2 text-left">
                                                            SKU
                                                          </th>
                                                        )}
                                                        <th className="px-4 py-2 text-left">
                                                          ALLOCATED QTY
                                                        </th>
                                                        <th className="px-4 py-2 text-left">
                                                          DURATION
                                                        </th>
                                                        <th className="px-4 py-2 text-center">
                                                          ACTION
                                                        </th>
                                                      </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-[#ECECEC] bg-white">
                                                      {activePromos.map(
                                                        (promo: any) => {
                                                          const variantIds =
                                                            item.variants?.map(
                                                              (v: any) => v.id,
                                                            ) || [];
                                                          const variantSkus =
                                                            item.variants?.map(
                                                              (v: any) => v.sku,
                                                            ) || [];

                                                          const matchingProducts =
                                                            promo.products?.filter(
                                                              (prod: any) =>
                                                                prod.productId ===
                                                                  item.id ||
                                                                prod.productId ===
                                                                  item.sku ||
                                                                variantIds.includes(
                                                                  prod.productId,
                                                                ) ||
                                                                variantSkus.includes(
                                                                  prod.productId,
                                                                ),
                                                            ) || [];

                                                          const totalAllocatedQty =
                                                            matchingProducts.reduce(
                                                              (
                                                                sum: number,
                                                                prod: any,
                                                              ) =>
                                                                sum +
                                                                (Number(
                                                                  prod.quantity,
                                                                ) || 0),
                                                              0,
                                                            );

                                                          const matchedVariantSkus =
                                                            matchingProducts
                                                              .map(
                                                                (prod: any) => {
                                                                  const matchedVariant =
                                                                    item.variants?.find(
                                                                      (
                                                                        v: any,
                                                                      ) =>
                                                                        v.id ===
                                                                          prod.productId ||
                                                                        v.sku ===
                                                                          prod.productId,
                                                                    );
                                                                  if (
                                                                    matchedVariant?.sku
                                                                  )
                                                                    return matchedVariant.sku;
                                                                  if (prod.sku)
                                                                    return prod.sku;
                                                                  if (
                                                                    item.variants?.some(
                                                                      (
                                                                        v: any,
                                                                      ) =>
                                                                        v.sku ===
                                                                        prod.productId,
                                                                    )
                                                                  )
                                                                    return prod.productId;
                                                                  if (
                                                                    item.variants?.some(
                                                                      (
                                                                        v: any,
                                                                      ) =>
                                                                        v.sku ===
                                                                        prod.productName,
                                                                    )
                                                                  )
                                                                    return prod.productName;
                                                                  return (
                                                                    prod.productId ||
                                                                    prod.productName ||
                                                                    ""
                                                                  );
                                                                },
                                                              )
                                                              .filter(Boolean);

                                                          const variantSkuDisplay =
                                                            Array.from(
                                                              new Set(
                                                                matchedVariantSkus,
                                                              ),
                                                            ).join(", ") ||
                                                            "-";

                                                          return (
                                                            <tr
                                                              key={promo.id}
                                                              onClick={() =>
                                                                handleViewPromo(
                                                                  promo,
                                                                )
                                                              }
                                                              className="hover:bg-slate-50/50 transition cursor-pointer"
                                                            >
                                                              <td className="px-4 py-2 font-semibold text-amber-600">
                                                                {
                                                                  promo.promoCode
                                                                }
                                                              </td>
                                                              {hasVariants && (
                                                                <td className="px-4 py-2 text-xs font-medium text-slate-700">
                                                                  {
                                                                    variantSkuDisplay
                                                                  }
                                                                </td>
                                                              )}
                                                              <td className="px-4 py-2 text-[#212121]">
                                                                {
                                                                  totalAllocatedQty
                                                                }{" "}
                                                                units
                                                              </td>
                                                              <td className="px-4 py-2 text-xs text-[#8A8A8A]">
                                                                {promo.startEndDate
                                                                  ? promo.startEndDate.replace(
                                                                      " - ",
                                                                      " to ",
                                                                    )
                                                                  : "-"}
                                                              </td>
                                                              <td className="px-4 py-2 flex justify-center">
                                                                <button
                                                                  onClick={(
                                                                    e,
                                                                  ) => {
                                                                    e.stopPropagation();
                                                                    handleViewPromo(
                                                                      promo,
                                                                    );
                                                                  }}
                                                                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECECEC] bg-white hover:bg-[#F9F9F9] transition"
                                                                  title="View Promotion"
                                                                >
                                                                  <IconEye
                                                                    size={16}
                                                                    className="text-[#404040]"
                                                                  />
                                                                </button>
                                                              </td>
                                                            </tr>
                                                          );
                                                        },
                                                      )}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              ) : (
                                                <div className="p-4 text-center border border-dashed border-[#ECECEC] rounded-xl text-xs text-[#8A8A8A] bg-white">
                                                  No active promotions for this
                                                  product.
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        )}

                                        {/* Tab 3: Terms & Conditions */}
                                        {activeTab === "terms" && (
                                          <div className="bg-[#F7F6F2]/40 rounded-2xl p-5 border border-[#ECECEC]">
                                            <h4 className="text-xs font-bold text-[#8A8A8A] uppercase tracking-wider mb-3">
                                              Terms & Conditions
                                            </h4>
                                            <div className="bg-white p-4 rounded-xl border border-[#ECECEC] max-h-60 overflow-y-auto">
                                              <span className="text-xs font-bold text-[#8A8A8A] uppercase">
                                                Policy Content
                                              </span>
                                              <p className="text-sm text-[#404040] mt-1 leading-relaxed whitespace-pre-line">
                                                {item.termsAndConditions ||
                                                  "No terms and conditions configured for this product."}
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="p-6">
                      <NoDataFound />
                    </div>
                  )}
                </div>
              </div>
            </div>

        {/* Right Column: Sibling Modals/Side Panels */}
        {isAnySidePanelOpen && (
          <div className="col-span-12 xl:col-span-5 lg:col-span-5 flex flex-col justify-start">
            {isAddProductOpen && (
              <div className="relative border rounded-lg border-gray-200 flex flex-col overflow-hidden bg-transparent">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-[#212121]/60 truncate">
                      {editingProductId ? "Edit Item" : "Add Item"}
                    </h2>
                    <div className="flex items-center gap-4">
                      {/* Create Promo Button inside side panel header */}
                      {canCreatePromo && (
                        <ButtonStyled
                          type="button"
                          onClick={handleAddPromo}
                          variant="outline"
                          value="Create Promo"
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {editingProductId
                      ? canUpdateProduct && (
                          <ButtonStyled
                            type="button"
                            onClick={() => addItemRef.current?.submit()}
                            variant="outline"
                            value="Update"
                          />
                        )
                      : canCreateProduct && (
                          <ButtonStyled
                            type="button"
                            onClick={() => addItemRef.current?.submit()}
                            variant="outline"
                            value="Save"
                          />
                        )}
                    <button
                      onClick={() => {
                        setIsAddProductOpen(false);
                        setIsEditMode(false);
                      }}
                      className="p-1.5 rounded-lg border border-[#D2D2D2] text-[#404040] hover:bg-gray-50 transition"
                    >
                      <IconX size={18} />
                    </button>
                  </div>
                </div>

                {/* AddItem component body */}
                <AddItem
                  ref={addItemRef}
                  itemId={editingProductId}
                  onClose={() => {
                    setIsAddProductOpen(false);
                    setIsEditMode(false);
                  }}
                  onSuccess={() => {
                    setIsAddProductOpen(false);
                    setIsEditMode(false);
                    refetch();
                  }}
                  inline={true}
                  defaultEditing={isEditMode}
                  hideHeader={true}
                />
              </div>
            )}

            {isPromoOpen && (
              <CreatePromoModal
                open={isPromoOpen}
                onClose={() => setIsPromoOpen(false)}
                setValue={setPromoValue}
                watch={watchPromo}
                register={registerPromo}
                errors={promoErrors}
                clearErrors={clearPromoErrors}
                onSubmit={handleSubmitPromo((vals) =>
                  onSubmitPromo(
                    vals,
                    promoModalMode === "add" ? "add" : "edit",
                  ),
                )}
                loading={promoCreating || promoUpdating}
                mode={promoModalMode}
                setMode={setPromoModalMode}
                inline={true}
              />
            )}
          </div>
        )}
      </div>
      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title="Confirmation Required"
        subtitle="Are you sure you want to perform this action?"
        img={
          selectedItem?.status === "ACTIVE"
            ? "/img/inactive.svg"
            : "/img/active.svg"
        }
        showFooter={true}
        closeButtonText="Cancel"
        actionButtonText="Confirm"
        actionButtonOnClick={confirmUpdateStatus}
        isPending={statusPending}
        size="md"
      />
    </>
  );
}
